import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { lastUsernameQuery, getDeviceToken } from '../utils/auth';
import { AUTH_URL } from '../utils/authApi';
import { qrStatus, serverFout } from '../utils/qrStatus';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8009';

// Convert base64url to ArrayBuffer
function base64urlToBuffer(base64url) {
  const padding = '='.repeat((4 - base64url.length % 4) % 4);
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding;
  const rawData = atob(base64);
  return Uint8Array.from(rawData, c => c.charCodeAt(0)).buffer;
}

// Convert ArrayBuffer to base64url
function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (const byte of bytes) {
    str += String.fromCharCode(byte);
  }
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

export function QrApprove() {
  const { code } = useParams();
  const [status, setStatus] = useState('loading'); // loading, ready, approving, approved, error, expired
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [error, setError] = useState('');

  const scanQrCode = useCallback(async () => {
    try {
      const { status: huidig, deviceInfo: info } = await qrStatus(code);

      if (huidig === 'pending') {
        setDeviceInfo(info);
        setStatus('ready');
      } else if (huidig === 'expired') {
        setStatus('expired');
      } else {
        setError('QR code ongeldig of verlopen');
        setStatus('error');
      }
    } catch (err) {
      const msg = serverFout(err, 'QR code ongeldig of verlopen');
      setError(msg);
      setStatus(msg.includes('expired') ? 'expired' : 'error');
    }
  }, [code]);

  useEffect(() => {
    scanQrCode();
  }, [scanQrCode]);

  const handleApprove = async () => {
    setStatus('approving');
    setError('');

    try {
      // Get WebAuthn authentication options for the user logged in on this phone
      // (was hardcoded to one username; now taken from the last login).
      const optionsResponse = await axios.get(`${AUTH_URL}/api/auth/webauthn/login-options${lastUsernameQuery()}`);
      const options = optionsResponse.data;

      // Prepare WebAuthn options
      const publicKeyOptions = {
        challenge: base64urlToBuffer(options.challenge),
        timeout: options.timeout,
        rpId: options.rpId,
        userVerification: options.userVerification,
      };

      if (options.allowCredentials) {
        publicKeyOptions.allowCredentials = options.allowCredentials.map(c => ({
          id: base64urlToBuffer(c.id),
          type: c.type,
          transports: c.transports,
        }));
      }

      // Trigger biometric prompt
      const credential = await navigator.credentials.get({
        publicKey: publicKeyOptions,
      });

      // Laravel approves on this phone's device token; the biometric prompt
      // above is the local gate.
      const deviceToken = getDeviceToken();
      const approveResponse = await axios.post(`${API_URL}/api/auth/qr/${code}/approve`, {
        credential: {
          id: credential.id,
          rawId: bufferToBase64url(credential.rawId),
          type: credential.type,
          response: {
            authenticatorData: bufferToBase64url(credential.response.authenticatorData),
            clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
            signature: bufferToBase64url(credential.response.signature),
          },
        },
      }, { headers: { Authorization: `Bearer ${deviceToken}` } });

      if (approveResponse.data.success) {
        setStatus('approved');
      }
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Biometrische verificatie geannuleerd');
        setStatus('ready'); // Allow retry
      } else {
        setError(serverFout(err, 'Goedkeuring mislukt'));
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-primary-50 to-primary-100">
      <div className="max-w-md w-full mx-4">
        <div className="card">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900">HavunCore</h1>
            <p className="text-gray-600 mt-2">QR Login Goedkeuren</p>
          </div>

          <div className="flex flex-col items-center space-y-6">
            {status === 'loading' && (
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
                <p className="mt-4 text-gray-600">QR code verwerken...</p>
              </div>
            )}

            {status === 'ready' && (
              <>
                {deviceInfo && (
                  <div className="w-full bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500 mb-1">Inloggen op:</p>
                    <p className="text-lg font-medium text-gray-900">
                      {deviceInfo.browser || 'Browser'} op {deviceInfo.os || 'Desktop'}
                    </p>
                  </div>
                )}

                <button
                  onClick={handleApprove}
                  className="w-full btn-primary flex items-center justify-center gap-3 text-lg py-4"
                >
                  <span className="text-2xl">🔐</span>
                  Goedkeuren met biometrie
                </button>

                <p className="text-xs text-gray-500 text-center">
                  Gebruik Face ID of vingerafdruk om de desktop-login goed te keuren
                </p>
              </>
            )}

            {status === 'approving' && (
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
                <p className="mt-4 text-gray-600">Biometrie verwerken...</p>
              </div>
            )}

            {status === 'approved' && (
              <div className="text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <span className="text-4xl">✅</span>
                </div>
                <p className="mt-4 text-lg font-medium text-green-600">Desktop is ingelogd!</p>
                <p className="mt-2 text-sm text-gray-500">Je kunt dit scherm sluiten</p>
              </div>
            )}

            {status === 'expired' && (
              <div className="text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                  <span className="text-4xl">⏰</span>
                </div>
                <p className="mt-4 text-lg font-medium text-red-600">QR code verlopen</p>
                <p className="mt-2 text-sm text-gray-500">Genereer een nieuwe QR code op de desktop</p>
              </div>
            )}

            {status === 'error' && (
              <div className="text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                  <span className="text-4xl">❌</span>
                </div>
                <p className="mt-4 text-lg font-medium text-red-600">{error}</p>
              </div>
            )}

            {error && status === 'ready' && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm w-full">
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
